import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { HeartHandshake, BookOpen, GraduationCap, Lock } from 'lucide-react';
import { getEntitlements } from '@/utils/entitlements';
import { isFreeFaithCategory } from '@/config/curriculum';

const GOLD = '#c8a84b';
const DARK = '#0f1a2b';
const DARK2 = '#0a1220';
const CARD = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const GREEN = '#4ade80';
const RED = '#f87171';

interface LessonMeta {
    title: string;
    learning_objective?: string;
    core_concepts?: string[];
}

interface ModuleMeta {
    unit: number;
    title: string;
    lessons: LessonMeta[];
}

interface EnrolledCourse {
    class_id: string;
    class_name: string;
    mastery_score: number;
    last_active_at: string;
    progress_state: { current_unit: number; current_lesson: number; total_lessons_completed: number };
    module_plan?: ModuleMeta[];
    essay_mode?: string | null;
    pending_reflections?: number[];
    submissions?: Array<{ module_id: number; ai_score: number | null; max_score: number | null; created_at?: string }>;
}

interface QuizResult {
    id: string;
    lesson_unit: number;
    lesson_number: number;
    score_percentage: number;
    duration_seconds: number | null;
    attempted_at: string;
    questions: QuizQuestion[];
    category_name?: string;
    class_name?: string;
}

interface QuizQuestion {
    question: string;
    options: string[];
    correct: string;
    student_answer: string;
    correct_yn: boolean;
}

interface SubjectProgressRow {
    id: string;
    category_id: string;
    class_id: string | null;
    current_unit: number | null;
    current_lesson: number | null;
    mastery_score: number | null;
    weak_concepts: string[] | null;
    lessons_completed: string[] | null;
    quiz_history: any[] | null;
    last_activity_at: string | null;
    completed_at: string | null;
    category_name?: string;
}

const SAGE_BIBLE_SCHOOL_ID = 'a0000000-0000-4000-a000-000000000001';

type AvailableCourse = {
    id: string;
    name: string;
    description: string | null;
    module_plan: any[] | null;
    lesson_count?: number | null;
    module_count?: number | null;
    is_enrolled: boolean;
    category_id?: string;
    category_type?: string;
    curriculum_track?: string | null;
    essay_mode?: string | null;
    pending_reflections?: number[];
    progress?: EnrolledCourse['progress_state'] | null;
}

const getMC = (s: number) => s >= 80 ? GREEN : s >= 60 ? GOLD : RED;
const getML = (s: number, t: any) => s >= 80 ? t('church.member.excellent') : s >= 60 ? t('church.member.good') : t('church.member.keepGoing');
const fmt = (d: string, t: any) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : t('church.member.never');
const scoreColor = (pct: number) => pct >= 90 ? '#059669' : pct >= 70 ? '#3b82f6' : pct >= 50 ? '#d97706' : '#dc2626';

type Tab = 'my_courses' | 'core_courses' | 'further' | 'progress' | 'subscription';

const cardVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.35 } }),
};

const ChurchIcon = ({ size = 42 }: { size?: number }) => (
    <div style={{
        width: size, height: size, background: '#0ea5e9', borderRadius: size * 0.28,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        boxShadow: '0 4px 12px rgba(14,165,233,0.2)'
    }}>
        <svg width={size * 0.45} height={size * 0.55} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4V20M7 10H17" stroke="white" strokeWidth="3" strokeLinecap="round" />
        </svg>
    </div>
);

export default function ChurchMemberPage() {
    const { user, userProfile, isLoading: authLoading, isFetchingProfile } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // ── Derived entitlements ──────────────────────────────────────────────────
    const entitlements = getEntitlements(userProfile as Record<string, any> | null);
    const bibleSchool = entitlements.bibleSchool;
    // ─────────────────────────────────────────────────────────────────────────

    const [enrolled, setEnrolled] = useState<EnrolledCourse[]>([]);
    const [available, setAvailable] = useState<AvailableCourse[]>([]);
    interface PathGroup { slug: string; title: string; description: string | null; courses: AvailableCourse[]; }
    const [pathGroups, setPathGroups] = useState<PathGroup[]>([]);
    const [churchName, setChurchName] = useState('Your Church');
    const [loading, setLoading] = useState(true);
    const [enrolling, setEnrolling] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<Tab>('my_courses');
    const [isMobile, setIsMobile] = useState(false);

    // Progress tab state
    const [quizResults, setQuizResults] = useState<QuizResult[]>([]);
    const [subjectProgress, setSubjectProgress] = useState<SubjectProgressRow[]>([]);
    const [progressLoading, setProgressLoading] = useState(false);
    const [progressLoaded, setProgressLoaded] = useState(false);
    const [expandedQuiz, setExpandedQuiz] = useState<string | null>(null);
    const [selectedProgressCourse, setSelectedProgressCourse] = useState<string | null>(null);
    const [loadingPlan, setLoadingPlan] = useState<'premium' | 'sponsorship' | 'portal' | null>(null);

    // Free Invite states
    const [freeInviteEmail, setFreeInviteEmail] = useState('');
    const [sendingFreeInvite, setSendingFreeInvite] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [seatPrice, setSeatPrice] = useState<number>(7);
    const [sponsorEmail, setSponsorEmail] = useState('');
    const [invitingSponsor, setInvitingSponsor] = useState(false);
    const [isSelfManaged, setIsSelfManaged] = useState<boolean>(false);
    const [sponsoredSeats, setSponsoredSeats] = useState<number>(0);
    const [sponsoredMembers, setSponsoredMembers] = useState<any[]>([]);

    // Donation state
    const [donationAmount, setDonationAmount] = useState<number>(25);
    const [donationType, setDonationType] = useState<'month' | 'once'>('once');
    const [donating, setDonating] = useState(false);
    const [showDonationPicker, setShowDonationPicker] = useState(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 820);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    useEffect(() => {
        if (authLoading) return;

        if (!user) {
            console.log('[ChurchMemberPage] 🚪 No user found, redirecting to sign-in.');
            try {
                localStorage.setItem('sage_faith_intent', '/church/learn');
                sessionStorage.setItem('sage_faith_intent', '/church/learn');
            } catch { }
            navigate('/signin?next=/church/learn', { replace: true });
            return;
        }

        // If auth finished but profile is still missing facility_id, 
        // we should either wait for profile or show an error.
        // We add a short delay or just stop loading if it's clear they aren't church-linked.
        if (user || !isFetchingProfile) {
            loadData();
        }
    }, [user?.id, userProfile?.facility_id, authLoading, isFetchingProfile]);

    // Load progress data once when user views progress tab
    useEffect(() => {
        if (activeTab === 'progress' && user?.id && !progressLoaded && !progressLoading) {
            loadProgressData();
        }
    }, [activeTab, user?.id, progressLoaded, progressLoading]);

    async function loadProgressData() {
        if (!user?.id) return;
        setProgressLoading(true);
        try {
            const [{ data: quizData }, { data: progressData }] = await Promise.all([
                supabase
                    .from('education_quiz_results')
                    .select('id, lesson_unit, lesson_number, score_percentage, duration_seconds, attempted_at, questions, category_id, class_id, categories(name), classes(name)')
                    .eq('user_id', user.id)
                    .order('attempted_at', { ascending: false })
                    .limit(50),
                supabase
                    .from('student_subject_progress')
                    .select('id, category_id, class_id, current_unit, current_lesson, mastery_score, weak_concepts, lessons_completed, quiz_history, last_activity_at, completed_at, categories(name)')
                    .eq('user_id', user.id)
                    .order('last_activity_at', { ascending: false }),
            ]);

            setQuizResults((quizData || []).map((q: any) => ({
                id: q.id,
                lesson_unit: q.lesson_unit,
                lesson_number: q.lesson_number,
                score_percentage: q.score_percentage,
                duration_seconds: q.duration_seconds,
                attempted_at: q.attempted_at,
                questions: (q.questions as QuizQuestion[]) || [],
                category_name: q.categories?.name || 'Unknown',
                class_name: q.classes?.name || undefined,
            })));

            setSubjectProgress((progressData || []).map((p: any) => ({
                ...p,
                weak_concepts: Array.isArray(p.weak_concepts) ? p.weak_concepts : [],
                lessons_completed: Array.isArray(p.lessons_completed) ? p.lessons_completed : [],
                quiz_history: Array.isArray(p.quiz_history) ? p.quiz_history : [],
                category_name: p.categories?.name || 'Unknown',
            })));
        } catch (err) {
            console.error('loadProgressData error:', err);
        } finally {
            setProgressLoading(false);
            setProgressLoaded(true);
        }
    }

    async function loadData() {
        console.log('[ChurchMemberPage] 🚀 loadData starting...', {
            uid: user?.id,
            fid: userProfile?.facility_id
        });

        if (!user?.id) {
            console.log('[ChurchMemberPage] ⚠️ Missing user info, early return.');
            setLoading(false);
            return;
        }

        // We still check for facility_id for facility-specific logic, but don't block the WHOLE page load
        const hasFacility = !!userProfile?.facility_id;

        if (enrolled.length === 0 && available.length === 0) {
            setLoading(true);
        }
        try {
            let facility: any = null;
            if (userProfile?.facility_id) {
                console.log('[ChurchMemberPage] 🧐 Fetching facility name...');
                const { data: facData } = await supabase.from('facilities').select('name, type, facility_type').eq('id', userProfile.facility_id).maybeSingle();
                facility = facData;
                console.log('[ChurchMemberPage] ✅ Facility resolved:', facility?.name);
                if (facility?.name) setChurchName(facility.name);
            }

            console.log('[ChurchMemberPage] 🧐 Fetching enrollments...');
            let { data: enrollments, error: enrollError } = await supabase
                .from('class_enrollments')
                .select('class_id, mastery_score, last_active_at, progress_state, classes(name, module_plan)')
                .eq('student_id', user.id);
            if (enrollError) console.error('[ChurchMemberPage] ❌ Enrollments fetch error:', enrollError);
            console.log('[ChurchMemberPage] ✅ Enrollments raw count:', enrollments?.length || 0);

            // B2 Guardrail: Auto-enrol only on successful query (!enrollError), strictly 0 enrollments, in church context
            const fType = facility?.facility_type || facility?.type || '';
            const isChurchContext = !userProfile?.facility_id || userProfile?.facility_id === '20000000-0000-4000-a000-000000000001' || fType === 'church' || fType === 'faith';

            if (!enrollError && (!enrollments || enrollments.length === 0) && isChurchContext) {
                console.log('[ChurchMemberPage] 🚀 Auto-enrolling church user in faith courses...');
                try {
                    await supabase.functions.invoke('enrol-faith');
                    const { data: refetched } = await supabase
                        .from('class_enrollments')
                        .select('class_id, mastery_score, last_active_at, progress_state, classes(name, module_plan)')
                        .eq('student_id', user.id);
                    if (refetched && refetched.length > 0) {
                        enrollments = refetched;
                    }
                } catch (autoErr) {
                    console.warn('[ChurchMemberPage] Auto-enrol attempt:', autoErr);
                }
            }

            console.log('[ChurchMemberPage] 🧐 Fetching church catalog and homework submissions...');
            const [{ data: allClasses, error: classesError }, { data: userSubmissions }] = await Promise.all([
                supabase
                    .from('classes')
                    .select('id, name, description, lesson_count, module_count, category_id, curriculum_track, essay_mode, categories(category_type)')
                    .eq('is_active', true)
                    .eq('in_church_catalog', true)
                    .order('name'),
                supabase
                    .from('university_submissions')
                    .select('id, class_id, module_id, ai_score, max_score, created_at')
                    .eq('user_id', user.id)
            ]);

            if (classesError) console.error('[ChurchMemberPage] ❌ Classes fetch error:', classesError);
            console.log('[ChurchMemberPage] ✅ Church catalog total:', allClasses?.length || 0);

            const progressByClass = new Map((enrollments || []).map(e => [e.class_id, (e.progress_state as any)]));
            const enrolledIds = new Set((enrollments || []).map(e => e.class_id));

            const submissionsByClass = new Map<string, Array<{ module_id: number; ai_score: number | null; max_score: number | null; created_at?: string }>>();
            (userSubmissions || []).forEach((s: any) => {
                if (!s.class_id || !s.module_id) return;
                const list = submissionsByClass.get(s.class_id) || [];
                list.push({ module_id: s.module_id, ai_score: s.ai_score, max_score: s.max_score, created_at: s.created_at });
                submissionsByClass.set(s.class_id, list);
            });

            const pendingByClass = new Map<string, number[]>();
            (enrollments || []).forEach((e: any) => {
                const cls = e.classes || {};
                const classCatalogItem = (allClasses || []).find(c => c.id === e.class_id);
                const essayMode = classCatalogItem?.essay_mode;
                const modPlan = cls.module_plan;
                if (essayMode === 'essay_per_module' && modPlan) {
                    const prog = progressByClass.get(e.class_id);
                    if (prog) {
                        const currentUnit = prog.current_unit || 1;
                        const submittedModules = new Set((submissionsByClass.get(e.class_id) || []).map(s => s.module_id));
                        const pending: number[] = [];
                        (modPlan as any[]).forEach((m: any) => {
                            const modId = m.module_id || m.unit;
                            if (modId && modId < currentUnit && !submittedModules.has(modId)) {
                                pending.push(modId);
                            }
                        });
                        if (pending.length > 0) {
                            pendingByClass.set(e.class_id, pending);
                        }
                    }
                }
            });

            const enrolledCourses: EnrolledCourse[] = (enrollments || []).map((e) => {
                const cls: any = e.classes || {};
                const clsId = e.class_id;
                const classCatalogItem = (allClasses || []).find(c => c.id === clsId);
                return {
                    class_id: clsId,
                    class_name: cls.name || 'Unknown',
                    mastery_score: Number(e.mastery_score) || 0,
                    last_active_at: e.last_active_at || '',
                    progress_state: (e.progress_state as any) || { current_unit: 1, current_lesson: 1, total_lessons_completed: 0 },
                    module_plan: (cls.module_plan as any[]) || null,
                    essay_mode: classCatalogItem?.essay_mode || null,
                    pending_reflections: pendingByClass.get(clsId) || [],
                    submissions: submissionsByClass.get(clsId) || [],
                };
            });
            setEnrolled(enrolledCourses);

            const availableWithMeta: AvailableCourse[] = (allClasses || []).map(c => ({
                id: c.id,
                name: c.name,
                description: c.description,
                module_plan: null,
                lesson_count: c.lesson_count ? Number(c.lesson_count) : null,
                module_count: c.module_count ? Number(c.module_count) : null,
                is_enrolled: enrolledIds.has(c.id),
                category_id: c.category_id,
                curriculum_track: c.curriculum_track ?? null,
                essay_mode: c.essay_mode ?? null,
                pending_reflections: pendingByClass.get(c.id) || [],
                category_type: (c.categories as any)?.category_type || 'chat',
                progress: enrolledIds.has(c.id) ? progressByClass.get(c.id) ?? null : null,
            }));

            setAvailable(availableWithMeta);
                            // ── Pathway groups (Further tab) ────────────────────────────────
        const { data: pathRows } = await supabase
            .from('course_paths')
            .select('id, slug, name, description')
            .neq('slug', 'bible-journey')           // Core tab already owns the Journey
            .order('display_order', { ascending: true });

        const { data: linkRows } = await supabase
            .from('course_path_courses')
            .select('path_id, class_id, sequence')
            .order('sequence', { ascending: true });

        if (pathRows && linkRows) {
            const byId = new Map(availableWithMeta.map(c => [c.id, c]));
            const groups: PathGroup[] = pathRows
                .map(p => ({
                    slug: p.slug,
                    title: p.name,                   // DB column is `name`
                    description: p.description,
                    courses: linkRows
                        .filter(l => l.path_id === p.id)
                        .map(l => byId.get(l.class_id))
                        .filter((c): c is AvailableCourse => Boolean(c)),
                }))
                .filter(g => g.courses.length > 0);
            setPathGroups(groups);
            console.log('[ChurchMemberPage] ✅ Pathway groups:', groups.map(g => `${g.title} (${g.courses.length})`).join(' · '));
        }
            // Fetch dynamic seat pricing
            const { data: planData } = await supabase
                .from('subscription_plans')
                .select('price_monthly')
                .eq('plan_key', 'sage-church-seat')
                .eq('is_active', true)
                .maybeSingle();
            if (planData) setSeatPrice(Number(planData.price_monthly) || 7);

            // Fetch self-managed sponsorships
            const { data: sponsorships } = await (supabase as any)
                .from('facility_seat_sponsorships')
                .select('quantity')
                .eq('sponsor_id', user.id)
                .eq('is_self_managed', true)
                .eq('status', 'active');

            const totalSelfManaged = (sponsorships || []).reduce((acc: number, s: any) => acc + (s.quantity || 0), 0);
            setSponsoredSeats(totalSelfManaged);

            const { data: members } = await supabase
                .from('users')
                .select('id, name, email, created_at')
                .eq('sponsor_id', user.id);
            setSponsoredMembers(members || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }

    async function enrolInCourse(classId: string) {
        setEnrolling(classId);
        try {
            // Invoke enrol-faith edge function (service role, bypasses student RLS)
            const { error: fnErr } = await supabase.functions.invoke('enrol-faith', {
                body: { class_id: classId }
            });
            if (fnErr) {
                console.error('[ChurchMemberPage] Enrol error:', fnErr);
                setMessage({ type: 'error', text: fnErr.message || 'Unable to enrol in course' });
                return;
            }
            await loadData();
        } catch (err: any) {
            console.error('[ChurchMemberPage] Unexpected enrol error:', err);
            setMessage({ type: 'error', text: err?.message || 'Unable to enrol in course' });
        } finally {
            setEnrolling(null);
        }
    }

    const totalLessons = enrolled.reduce((s, e) => s + (e.progress_state?.total_lessons_completed || 0), 0);
    const avgMastery = enrolled.length ? Math.round(enrolled.reduce((s, e) => s + e.mastery_score, 0) / enrolled.length) : 0;

    // Journey classes (faith track) sorted sequentially by course ID (...0101 -> ...0109)
    const bibleschoolAvailable = available
        .filter(c => c.curriculum_track === 'faith')
        .sort((a, b) => (a.id.slice(-4) > b.id.slice(-4) ? 1 : -1));

    const furtherAvailable = available.filter(c => c.curriculum_track !== 'faith');

    const TABS = [
        { key: 'my_courses' as Tab, label: `${t('church.member.myCourses')} (${enrolled.length})` },
        { key: 'progress' as Tab, label: `📊 ${t('church.member.progress')}` },
        { key: 'core_courses' as Tab, label: `${t('church.member.coreCourses')} (${bibleschoolAvailable.length})` },
        { key: 'further' as Tab, label: `${t('church.member.furtherStudies')} (${furtherAvailable.length})` },
        { key: 'subscription' as Tab, label: `💳 ${t('church.member.subscription')}` },
    ];

    async function startCheckout(type: 'personal' | 'sponsorship' = 'personal', quantity: number = 1) {
        setLoadingPlan(type === 'sponsorship' ? 'sponsorship' : 'premium');
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('No session');

            const { data, error } = await supabase.functions.invoke('billing', {
                body: {
                    action: 'checkout',
                    plan: 'sage-church-seat',
                    interval: 'month',
                    quantity: quantity,
                    destination: type === 'sponsorship' ? 'sponsorship' : 'church_member',
                    facility_id: userProfile?.facility_id,
                    metadata: type === 'sponsorship' ? { is_self_managed: String(isSelfManaged) } : {}
                },
                headers: { Authorization: `Bearer ${session.access_token}` },
            });

            if (error) throw error;
            if (data?.url) window.location.href = data.url;
        } catch (e) {
            console.error('Checkout error', e);
            setMessage({ type: 'error', text: 'Unable to start checkout. Please try again.' });
        } finally {
            setLoadingPlan(null);
        }
    }

    async function openPortal() {
        setLoadingPlan('portal');
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('No session');

            const { data, error } = await supabase.functions.invoke('billing', {
                body: { action: 'portal' },
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (error) throw error;
            if (data?.url) window.location.href = data.url;
        } catch (e) {
            console.error('Portal error', e);
            setMessage({ type: 'error', text: 'Unable to open billing portal.' });
        } finally {
            setLoadingPlan(null);
        }
    }

    if (authLoading || loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: DARK2, flexDirection: 'column', gap: 16 }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                    style={{ width: 42, height: 42, borderRadius: '50%', border: '3px solid rgba(200,168,75,0.2)', borderTopColor: GOLD }} />
                <p style={{ color: GOLD, fontSize: 14, fontFamily: 'sans-serif' }}>{t('church.member.loading')}</p>
            </div>
        );
    }

    // Compute strengths / weaknesses from quiz results
    const strengths: string[] = [];
    const weaknesses: string[] = [];
    if (quizResults.length > 0) {
        const topicScores = new Map<string, { correct: number; total: number }>();
        for (const qr of quizResults) {
            const key = qr.class_name || qr.category_name || 'General';
            if (!topicScores.has(key)) topicScores.set(key, { correct: 0, total: 0 });
            const entry = topicScores.get(key)!;
            for (const q of qr.questions) {
                entry.total++;
                if (q.correct_yn) entry.correct++;
            }
        }
        for (const [topic, { correct, total }] of topicScores) {
            const pct = total > 0 ? (correct / total) * 100 : 0;
            if (pct >= 75) strengths.push(topic);
            else if (pct < 60) weaknesses.push(topic);
        }
    }
    // Also add weak_concepts from subject progress
    for (const sp of subjectProgress) {
        if (sp.weak_concepts?.length) {
            for (const c of sp.weak_concepts) {
                if (!weaknesses.includes(c)) weaknesses.push(c);
            }
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                background: DARK2,
                minHeight: '100vh',
                color: '#fff',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch'
            }}
        >
            {/* TOP BAR */}
            <div style={{
                background: 'rgba(15,26,43,0.95)', borderBottom: `1px solid ${BORDER}`,
                padding: '14px 32px', display: 'flex', alignItems: 'center', gap: 16,
                position: 'sticky', top: 0, zIndex: 50, backdropFilter: 'blur(10px)',
            }}>
                <div style={{ width: 28, height: 28, background: GOLD, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: DARK, fontWeight: 'bold', fontSize: 14 }}>S</span>
                </div>
                <span style={{ fontWeight: 700 }}>Sage</span>
                <span style={{ color: 'rgba(255,255,255,0.2)' }}>|</span>
                <span style={{ color: GOLD, fontSize: 14 }}>⛪ {churchName}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <motion.button
                        whileHover={{ borderColor: GOLD }}
                        onClick={() => navigate('/record/me?context=church')}
                        style={{ background: 'none', border: `1px solid ${BORDER}`, color: GOLD, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, transition: 'border-color 0.15s' }}
                    >
                        My Journey Record
                    </motion.button>
                    <motion.button
                        whileHover={{ borderColor: 'rgba(255,255,255,0.3)' }}
                        onClick={() => navigate('/profile')}
                        style={{ background: 'none', border: `1px solid ${BORDER}`, color: '#aaa', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, transition: 'border-color 0.15s' }}
                    >
                        {t('church.member.account')}
                    </motion.button>
                </div>
            </div>

            <div style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 24px' }}>

                {/* WELCOME */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{ marginBottom: 32 }}
                >
                    <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-1px', marginBottom: 6, color: '#fff' }}>
                        {t('church.member.welcome')}, {userProfile?.name?.split(' ')[0] || 'Friend'} 👋
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }}>{t('church.member.subtitle')} {churchName}</p>
                </motion.div>

                {/* STATS */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 16,
                    marginBottom: 28
                }}>
                    {[
                        { icon: '📚', label: t('church.member.statsCourses'), value: enrolled.length, color: '#60a5fa' },
                        { icon: '✅', label: t('church.member.statsLessons'), value: totalLessons, color: '#34d399' },
                        { icon: '🎯', label: t('church.member.statsMastery'), value: `${avgMastery}%`, color: GOLD },
                        { icon: '📝', label: t('church.member.quizzesTaken'), value: quizResults.length || '—', color: '#a78bfa' },
                    ].map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            custom={i}
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            whileHover={{ y: -3 }}
                            style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: '22px', transition: 'transform 0.15s' }}
                        >
                            <div style={{ fontSize: 24, marginBottom: 10 }}>{stat.icon}</div>
                            <div style={{ fontSize: 30, fontWeight: 800, color: stat.color, letterSpacing: '-1px' }}>{stat.value}</div>
                            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{stat.label}</div>
                        </motion.div>
                    ))}
                </div>

                {/* SUBTLE SUPPORT CARD — collapsible */}
                {!localStorage.getItem('sage_dismiss_support') && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{
                            background: 'rgba(200,168,75,0.04)',
                            border: '1px solid rgba(200,168,75,0.12)',
                            borderRadius: 14,
                            padding: '14px 20px',
                            marginBottom: 24,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 14,
                            fontSize: 13,
                            flexWrap: 'wrap' as const,
                        }}
                    >
                        <HeartHandshake size={18} color={GOLD} style={{ flexShrink: 0 }} />
                        <span style={{ color: 'rgba(255,255,255,0.6)', flex: 1, lineHeight: 1.4, minWidth: 180 }}>
                            {t('church.landing.supportText', 'We are doing our best to keep the price as low as we possibly can. Any support would be appreciated.')}
                        </span>
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                            <button
                                onClick={() => setShowDonationPicker(!showDonationPicker)}
                                style={{ background: GOLD, color: DARK, border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                            >{t('church.member.supportUs')}</button>
                        </div>
                        <button
                            onClick={() => { localStorage.setItem('sage_dismiss_support', '1'); window.location.reload(); }}
                            style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}
                            title="Dismiss"
                        >×</button>
                        {showDonationPicker && (
                            <div style={{ width: '100%', marginTop: 12, padding: '16px', background: 'rgba(200,168,75,0.08)', borderRadius: 12, border: `1px solid rgba(200,168,75,0.2)` }}>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                    {['once', 'month'].map(type => (
                                        <button
                                            key={type}
                                            onClick={() => setDonationType(type as 'once' | 'month')}
                                            style={{
                                                background: donationType === type ? GOLD : 'transparent',
                                                color: donationType === type ? DARK : '#ccc',
                                                border: `1px solid ${GOLD}40`, borderRadius: 8,
                                                padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                            }}
                                        >
                                            {type === 'once' ? t('church.member.oneTimeGift') : t('church.member.monthly')}
                                        </button>
                                    ))}
                                </div>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                                    {[10, 25, 50, 100].map(amt => (
                                        <button
                                            key={amt}
                                            onClick={() => setDonationAmount(amt)}
                                            style={{
                                                background: donationAmount === amt ? GOLD : 'rgba(255,255,255,0.06)',
                                                color: donationAmount === amt ? DARK : '#ccc',
                                                border: `1px solid ${donationAmount === amt ? GOLD : BORDER}`, borderRadius: 8,
                                                padding: '8px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', minWidth: 60,
                                            }}
                                        >
                                            ${amt}
                                        </button>
                                    ))}
                                    <input
                                        type="number"
                                        min="1"
                                        value={donationAmount}
                                        onChange={e => setDonationAmount(Math.max(1, Number(e.target.value)))}
                                        style={{
                                            width: 80, padding: '8px 10px', borderRadius: 8, border: `1px solid ${BORDER}`,
                                            background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: 14, outline: 'none', textAlign: 'center',
                                        }}
                                        placeholder="$"
                                    />
                                </div>
                                <button
                                    onClick={async () => {
                                        setDonating(true);
                                        try {
                                            const { data: { session } } = await supabase.auth.getSession();
                                            const { data, error } = await supabase.functions.invoke('billing', {
                                                body: { action: 'checkout', plan: donationType === 'month' ? 'donation_monthly' : 'donation_once', interval: donationType, destination: 'donation', amount: donationAmount },
                                                headers: { Authorization: `Bearer ${session?.access_token}` },
                                            });
                                            if (error) throw error;
                                            if (data?.url) window.location.href = data.url;
                                        } catch (e) { console.error('Donate error', e); }
                                        finally { setDonating(false); }
                                    }}
                                    disabled={donating}
                                    style={{
                                        background: GOLD, color: DARK, border: 'none', borderRadius: 10,
                                        padding: '10px 24px', fontSize: 14, fontWeight: 800, cursor: 'pointer',
                                        opacity: donating ? 0.6 : 1,
                                    }}
                                >
                                    {donating ? t('church.member.donating') : t('church.member.donateBtn', { amount: donationAmount, period: donationType === 'month' ? '/mo' : '' })}
                                </button>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* TABS */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 4, border: `1px solid ${BORDER}`, overflowX: 'auto', width: '100%' }}>
                    {TABS.map(tab => (
                        <motion.button
                            key={tab.key}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                background: activeTab === tab.key ? GOLD : 'transparent',
                                color: activeTab === tab.key ? DARK : '#888',
                                border: 'none', padding: '9px 20px', borderRadius: 9,
                                cursor: 'pointer', fontSize: 14, fontWeight: 700, transition: 'all 0.15s',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {tab.label}
                        </motion.button>
                    ))}
                </div>

                <AnimatePresence mode="wait">

                    {/* MY COURSES */}
                    {activeTab === 'my_courses' && (
                        <motion.div
                            key="my"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
                        >
                            {enrolled.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                                    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 24, padding: '42px 32px', textAlign: 'center' }}>
                                        <div style={{ fontSize: 42, marginBottom: 16 }}>📖</div>
                                        <div style={{ fontWeight: 800, fontSize: 22, color: '#fff', marginBottom: 12 }}>{t('church.member.startJourney', 'Start Your Learning Journey')}</div>
                                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 15, marginBottom: 0, maxWidth: 500, margin: '0 auto' }}>
                                            {t('church.member.noCoursesHintLong', 'You haven\'t enrolled in any courses yet. Choose your first Bible study from the catalog below to begin.')}
                                        </div>
                                    </div>

                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                                            <div style={{ width: 32, height: 32, background: GOLD, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <BookOpen size={18} color={DARK} />
                                            </div>
                                            <h3 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{t('church.member.featuredCourses', 'Featured Bible Studies')}</h3>
                                        </div>

                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                                            gap: 16
                                        }}>
                                            {bibleschoolAvailable.map((c, idx) => (
                                                <CourseBrowseCard
                                                    key={c.id}
                                                    course={c}
                                                    onEnrol={() => enrolInCourse(c.id)}
                                                    enrolling={enrolling === c.id}
                                                    enrolled={c.is_enrolled}
                                                    idx={idx}
                                                    navigate={navigate}
                                                    isMobile={isMobile}
                                                    bibleSchool={bibleSchool}
                                                    onUpgrade={() => startCheckout('personal')}
                                                />
                                            ))}
                                        </div>

                                        {pathGroups.map(group => (
                                            <div key={group.slug} style={{ marginBottom: 40 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                            <div style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.1)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <GraduationCap size={18} color="#fff" />
                                            </div>
                                            <h3 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{group.title}</h3>
                                            <span style={{ fontSize: 13, color: '#888' }}>({group.courses.length})</span>
                                        </div>
                                        {group.description && <p style={{ color: '#888', fontSize: 14, margin: '0 0 16px 44px' }}>{group.description}</p>}
                                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                                            {group.courses.map((c, idx) => (
                                                <CourseBrowseCard
                                                    key={c.id}
                                                    course={c}
                                                    onEnrol={() => enrolInCourse(c.id)}
                                                    enrolling={enrolling === c.id}
                                                    enrolled={c.is_enrolled}
                                                    idx={idx}
                                                    navigate={navigate}
                                                    isMobile={isMobile}
                                                    bibleSchool={bibleSchool}
                                                    onUpgrade={() => startCheckout('personal')}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                                    </div>
                                </div>
                            ) : enrolled.map((course, idx) => (
                                <motion.div
                                    key={course.class_id}
                                    custom={idx}
                                    variants={cardVariants}
                                    initial="hidden"
                                    animate="visible"
                                    whileHover={{ y: -2, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}
                                    style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 18, padding: '24px', display: 'flex', alignItems: 'center', gap: 20, transition: 'transform 0.15s, box-shadow 0.15s' }}
                                >
                                    <ChurchIcon size={52} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                                            <div style={{ fontWeight: 700, fontSize: 16 }}>{course.class_name}</div>
                                            {course.pending_reflections && course.pending_reflections.length > 0 && (
                                                <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
                                                    {course.pending_reflections.map(modNum => (
                                                        <span
                                                            key={modNum}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/church/session/${course.class_id}?module=${modNum}&lesson=1&reflection=1`);
                                                            }}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 4,
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                padding: '2px 8px',
                                                                borderRadius: 6,
                                                                background: 'rgba(200, 168, 75, 0.12)',
                                                                border: '1px solid rgba(200, 168, 75, 0.35)',
                                                                color: GOLD,
                                                                cursor: 'pointer',
                                                            }}
                                                            title={t('church.member.writeReflection')}
                                                        >
                                                            📝 {t('church.member.reflectionPending', { module: modNum })} →
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 13, color: '#444', marginBottom: 14 }}>
                                            Unit {course.progress_state?.current_unit}, Lesson {(() => {
                                                const modPlan = course.module_plan;
                                                const currUnit = course.progress_state?.current_unit || 1;
                                                const currLesson = course.progress_state?.current_lesson || 1;
                                                if (!modPlan) return currLesson;
                                                const mod = modPlan.find((m: any) => m.module_id === currUnit);
                                                if (!mod) return currLesson;
                                                const idx = (mod.lessons || []).findIndex((l: any) => l.lesson_id === currLesson);
                                                return idx >= 0 ? idx + 1 : currLesson;
                                            })()}
                                            · {course.progress_state?.total_lessons_completed || 0} {t('church.member.lessonsDone')}
                                            · {t('church.member.lastStudied')} {fmt(course.last_active_at, t)}
                                        </div>
                                        <div style={{ display: 'flex', gap: 16, alignItems: 'center', background: 'rgba(0,0,0,0.15)', padding: 12, borderRadius: 12 }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#aaa', marginBottom: 6, fontWeight: 700 }}>
                                                    <span>{t('church.member.progressBarLabel')}</span>
                                                    <span style={{ color: GOLD }}>
                                                        {Math.round(course.module_plan ? Math.min(((course.progress_state?.total_lessons_completed || 0) / Math.max(1, course.module_plan.reduce((a: number, m: any) => a + (m.lessons || []).length, 0))) * 100, 100) : Math.min((course.progress_state?.total_lessons_completed || 0) * 12, 100))}%
                                                    </span>
                                                </div>
                                                <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 100, height: 6, overflow: 'hidden' }}>
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${course.module_plan ? Math.min(((course.progress_state?.total_lessons_completed || 0) / Math.max(1, course.module_plan.reduce((a: number, m: any) => a + (m.lessons || []).length, 0))) * 100, 100) : Math.min((course.progress_state?.total_lessons_completed || 0) * 12, 100)}%` }}
                                                        transition={{ duration: 0.8, ease: 'easeOut', delay: idx * 0.06 }}
                                                        style={{ height: '100%', background: GOLD, borderRadius: 100 }}
                                                    />
                                                </div>
                                            </div>
                                            <div style={{ width: 1, height: 30, background: 'rgba(255,255,255,0.05)' }} />
                                            <div style={{ textAlign: 'right', minWidth: 80 }}>
                                                <div style={{ fontWeight: 800, fontSize: 18, color: getMC(course.mastery_score) }}>{Math.round(course.mastery_score || 0)}%</div>
                                                <div style={{ fontSize: 10, color: '#555', fontWeight: 600, textTransform: 'uppercase' }}>{t('church.member.masteryBarLabel')}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <motion.button
                                        whileHover={{ scale: 1.04, boxShadow: '0 4px 16px rgba(200,168,75,0.3)' }}
                                        whileTap={{ scale: 0.96 }}
                                        onClick={() => navigate(`/church/session/${course.class_id}?module=${course.progress_state?.current_unit || 1}&lesson=${course.progress_state?.current_lesson || 1}`)}
                                        style={{ background: GOLD, color: DARK, border: 'none', padding: '12px 22px', borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: 700, flexShrink: 0 }}
                                    >
                                        {t('church.member.continue')} →
                                    </motion.button>
                                </motion.div>
                            ))}
                        </motion.div>
                    )}

                    {/* PROGRESS TAB - DRILL DOWN */}
                    {activeTab === 'progress' && (
                        <motion.div
                            key="progress"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
                        >
                            {progressLoading ? (
                                <div style={{ textAlign: 'center', padding: 60 }}>
                                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                                        style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(200,168,75,0.2)', borderTopColor: GOLD, margin: '0 auto 14px' }} />
                                    <p style={{ color: '#555', fontSize: 14 }}>{t('church.member.loadingProgress')}</p>
                                </div>
                            ) : enrolled.length === 0 ? (
                                <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 18, padding: '60px 24px', textAlign: 'center' }}>
                                    <div style={{ fontSize: 48, marginBottom: 16 }}>📚</div>
                                    <p style={{ color: '#555', fontSize: 14 }}>{t('church.member.enrolPrompt')}</p>
                                </div>
                            ) : (
                                <>
                                    <p style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>{t('church.member.clickCourseHint')}</p>
                                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                                        {enrolled.map((course, idx) => {
                                            const sp = subjectProgress.find(s => s.class_id === course.class_id);
                                            const totalLessons = course.module_plan
                                                ? course.module_plan.reduce((a: number, m: any) => a + (m.lessons || []).length, 0)
                                                : 25;
                                            const completed = course.progress_state?.total_lessons_completed || 0;
                                            const pct = Math.min((completed / Math.max(1, totalLessons)) * 100, 100);
                                            const mastery = course.mastery_score || 0;
                                            const courseQuizCount = quizResults.filter(qr => qr.class_name === course.class_name).length;

                                            return (
                                                <motion.div
                                                    key={course.class_id}
                                                    custom={idx}
                                                    variants={cardVariants}
                                                    initial="hidden"
                                                    animate="visible"
                                                    whileHover={{ y: -3, boxShadow: `0 12px 40px rgba(200,168,75,0.15)` }}
                                                    onClick={() => setSelectedProgressCourse(course.class_id)}
                                                    style={{
                                                        background: CARD, border: `1px solid ${BORDER}`, borderRadius: 18,
                                                        padding: '24px', cursor: 'pointer', transition: 'border-color 0.2s',
                                                        borderColor: selectedProgressCourse === course.class_id ? GOLD : BORDER,
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
                                                        <ChurchIcon size={44} />
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3, marginBottom: 4 }}>{course.class_name}</div>
                                                            <div style={{ fontSize: 12, color: '#555' }}>
                                                                Unit {course.progress_state?.current_unit || 1}, Lesson {course.progress_state?.current_lesson || 1}
                                                                &nbsp;·&nbsp;{t('church.member.lessonsOfTotalShort', { completed, total: totalLessons })}
                                                            </div>
                                                        </div>
                                                        <div style={{ fontSize: 12, color: GOLD, fontWeight: 700, background: 'rgba(200,168,75,0.1)', padding: '4px 10px', borderRadius: 20, flexShrink: 0 }}>
                                                            {courseQuizCount} {courseQuizCount === 1 ? 'quiz' : 'quizzes'}
                                                        </div>
                                                    </div>

                                                    <div style={{ marginBottom: 10 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 700, color: '#666', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            <span>{t('church.member.completion')}</span><span style={{ color: GOLD }}>{Math.round(pct)}%</span>
                                                        </div>
                                                        <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 100, height: 6, overflow: 'hidden' }}>
                                                            <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: 'easeOut', delay: idx * 0.05 }}
                                                                style={{ height: '100%', background: GOLD, borderRadius: 100 }} />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 700, color: '#666', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            <span>{t('church.member.masteryLabel')}</span><span style={{ color: getMC(mastery) }}>{Math.round(mastery)}%</span>
                                                        </div>
                                                        <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 100, height: 6, overflow: 'hidden' }}>
                                                            <motion.div initial={{ width: 0 }} animate={{ width: `${mastery}%` }} transition={{ duration: 0.8, ease: 'easeOut', delay: idx * 0.05 + 0.1 }}
                                                                style={{ height: '100%', background: getMC(mastery), borderRadius: 100 }} />
                                                        </div>
                                                    </div>

                                                    <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div style={{ fontSize: 11, color: '#444' }}>{t('church.member.lastStudied')} {fmt(course.last_active_at, t)}</div>
                                                        <div style={{ fontSize: 12, color: GOLD, fontWeight: 700 }}>{t('church.member.viewDetailsArrow')}</div>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {/* LEVEL 2: COURSE DETAIL MODAL */}
                            <AnimatePresence>
                                {selectedProgressCourse && (() => {
                                    const course = enrolled.find(c => c.class_id === selectedProgressCourse);
                                    const sp = subjectProgress.find(s => s.class_id === selectedProgressCourse);
                                    if (!course) return null;

                                    const totalLessons = course.module_plan
                                        ? course.module_plan.reduce((a: number, m: any) => a + (m.lessons || []).length, 0)
                                        : 25;
                                    const completed = course.progress_state?.total_lessons_completed || 0;
                                    const pct = Math.min((completed / Math.max(1, totalLessons)) * 100, 100);
                                    const mastery = course.mastery_score || 0;

                                    // Course-specific quiz results
                                    const courseQuizzes = quizResults.filter(qr => qr.class_name === course.class_name);

                                    // Course-specific strengths & weaknesses
                                    const cStrengths: string[] = [];
                                    const cWeaknesses: string[] = [];
                                    const topicMap = new Map<string, { correct: number; total: number }>();
                                    for (const qr of courseQuizzes) {
                                        const key = `U${qr.lesson_unit}·L${qr.lesson_number}`;
                                        if (!topicMap.has(key)) topicMap.set(key, { correct: 0, total: 0 });
                                        const entry = topicMap.get(key)!;
                                        for (const q of qr.questions) { entry.total++; if (q.correct_yn) entry.correct++; }
                                    }
                                    for (const [topic, { correct, total }] of topicMap) {
                                        const p = total > 0 ? (correct / total) * 100 : 0;
                                        if (p >= 75) cStrengths.push(topic);
                                        else if (p < 60) cWeaknesses.push(topic);
                                    }
                                    if (sp?.weak_concepts?.length) {
                                        for (const wc of sp.weak_concepts) {
                                            if (!cWeaknesses.includes(wc)) cWeaknesses.push(wc);
                                        }
                                    }

                                    return (
                                        <motion.div
                                            key="progress-modal-overlay"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            onClick={() => setSelectedProgressCourse(null)}
                                            style={{
                                                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
                                                zIndex: 1000, display: 'flex', alignItems: 'flex-end',
                                                backdropFilter: 'blur(4px)',
                                            }}
                                        >
                                            <motion.div
                                                key="progress-modal-panel"
                                                initial={{ y: '100%' }}
                                                animate={{ y: 0 }}
                                                exit={{ y: '100%' }}
                                                transition={{ type: 'spring', damping: 30, stiffness: 260 }}
                                                onClick={e => e.stopPropagation()}
                                                style={{
                                                    width: '100%', maxHeight: '90vh', overflowY: 'auto',
                                                    background: '#0f1a2b', borderTopLeftRadius: 28, borderTopRightRadius: 28,
                                                    border: `1px solid ${BORDER}`, borderBottom: 'none',
                                                    padding: isMobile ? '24px 16px' : '32px 36px',
                                                    boxShadow: '0 -20px 60px rgba(0,0,0,0.6)',
                                                }}
                                            >
                                                {/* Header */}
                                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
                                                    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                                                        <ChurchIcon size={48} />
                                                        <div>
                                                            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 2 }}>{course.class_name}</div>
                                                            <div style={{ fontSize: 12, color: '#555' }}>
                                                                {t('church.member.lessonsOfTotalShort', { completed, total: totalLessons })} {t('church.member.completed').toLowerCase()} · {t('church.member.lastStudied')} {fmt(course.last_active_at, t)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => setSelectedProgressCourse(null)}
                                                        style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${BORDER}`, borderRadius: 10, color: '#aaa', fontSize: 18, width: 38, height: 38, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    >✕</button>
                                                </div>

                                                {/* Progress bars */}
                                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 28 }}>
                                                    {[{ label: t('church.member.courseCompletion'), value: pct, color: GOLD, hint: t('church.member.lessonsOfTotalShort', { completed, total: totalLessons }) },
                                                    { label: t('church.member.masteryScoreLabel'), value: mastery, color: getMC(mastery), hint: t('church.member.masteryHint') }].map(bar => (
                                                        <div key={bar.label} style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, borderRadius: 14, padding: '18px 20px' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                                <span style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{bar.label}</span>
                                                                <span style={{ fontSize: 22, fontWeight: 900, color: bar.color }}>{Math.round(bar.value)}%</span>
                                                            </div>
                                                            <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 100, height: 8, overflow: 'hidden', marginBottom: 8 }}>
                                                                <motion.div initial={{ width: 0 }} animate={{ width: `${bar.value}%` }} transition={{ duration: 0.9, ease: 'easeOut' }}
                                                                    style={{ height: '100%', background: bar.color, borderRadius: 100 }} />
                                                            </div>
                                                            <div style={{ fontSize: 11, color: '#444' }}>{bar.hint}</div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Strengths & Weaknesses */}
                                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 28 }}>
                                                    <div style={{ background: 'rgba(74,222,128,0.04)', border: '1px solid rgba(74,222,128,0.15)', borderRadius: 14, padding: '18px 20px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                                            <span style={{ fontSize: 18 }}>💪</span>
                                                            <span style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{t('church.member.strengths')}</span>
                                                        </div>
                                                        {cStrengths.length === 0 ? (
                                                            <p style={{ color: '#444', fontSize: 12, margin: 0 }}>{t('church.member.noStrengthsYet')}</p>
                                                        ) : (
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                                {cStrengths.map(s => (
                                                                    <span key={s} style={{ fontSize: 11, fontWeight: 600, background: 'rgba(74,222,128,0.12)', color: GREEN, padding: '4px 10px', borderRadius: 20, border: '1px solid rgba(74,222,128,0.2)' }}>
                                                                        ✓ {s}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ background: 'rgba(248,113,113,0.04)', border: '1px solid rgba(248,113,113,0.15)', borderRadius: 14, padding: '18px 20px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                                            <span style={{ fontSize: 18 }}>📖</span>
                                                            <span style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{t('church.member.areasToImprove')}</span>
                                                        </div>
                                                        {cWeaknesses.length === 0 ? (
                                                            <p style={{ color: '#444', fontSize: 12, margin: 0 }}>{t('church.member.noWeaknessesYet')}</p>
                                                        ) : (
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                                {cWeaknesses.map(w => (
                                                                    <span key={w} style={{ fontSize: 11, fontWeight: 600, background: 'rgba(248,113,113,0.12)', color: RED, padding: '4px 10px', borderRadius: 20, border: '1px solid rgba(248,113,113,0.2)' }}>
                                                                        ✗ {w}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Quiz Results for this course */}
                                                <div>
                                                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#aaa', marginBottom: 14 }}>📝 {t('church.member.quizResultsCourse')}</h3>
                                                    {courseQuizzes.length === 0 ? (
                                                        <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 14, padding: '32px', textAlign: 'center' }}>
                                                            <p style={{ color: '#444', fontSize: 13 }}>{t('church.member.noQuizResultsCourse')}</p>
                                                        </div>
                                                    ) : (
                                                        <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 14, overflow: 'hidden' }}>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                                <thead>
                                                                    <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                                                                        {[t('church.member.tableColDate'), t('church.member.tableColPosition'), t('church.member.tableColScore'), ''].map(h => (
                                                                            <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#555', letterSpacing: '0.8px' }}>{h}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {courseQuizzes.map((qr) => (
                                                                        <>
                                                                            <tr key={qr.id} onClick={() => setExpandedQuiz(expandedQuiz === qr.id ? null : qr.id)}
                                                                                style={{ cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                                                                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{new Date(qr.attempted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</td>
                                                                                <td style={{ padding: '12px 16px', fontSize: 13, color: '#888' }}>U{qr.lesson_unit} · L{qr.lesson_number}</td>
                                                                                <td style={{ padding: '12px 16px' }}>
                                                                                    <span style={{ fontSize: 13, fontWeight: 800, color: scoreColor(qr.score_percentage), background: `${scoreColor(qr.score_percentage)}20`, padding: '3px 12px', borderRadius: 20 }}>
                                                                                        {Math.round(qr.score_percentage)}%
                                                                                    </span>
                                                                                </td>
                                                                                <td style={{ padding: '12px 16px', color: '#555' }}>{expandedQuiz === qr.id ? '▼' : '▶'}</td>
                                                                            </tr>
                                                                            {expandedQuiz === qr.id && (
                                                                                <tr key={`${qr.id}-d`}>
                                                                                    <td colSpan={4} style={{ padding: 0 }}>
                                                                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                                                                                            style={{ overflow: 'hidden', padding: '14px 18px', background: 'rgba(255,255,255,0.02)', borderBottom: `1px solid ${BORDER}` }}>
                                                                                            <p style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 10 }}>
                                                                                                {qr.questions.filter(q => q.correct_yn).length}/{qr.questions.length} {t('church.member.correct')}
                                                                                                {qr.duration_seconds ? ` · ${Math.round(qr.duration_seconds / 60)} ${t('church.member.minutes')}` : ''}
                                                                                            </p>
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                                                                {qr.questions.map((q, qi) => (
                                                                                                    <div key={qi} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '10px 14px', borderLeft: `3px solid ${q.correct_yn ? GREEN : '#dc2626'}` }}>
                                                                                                        <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                                                                                                            <span style={{ color: q.correct_yn ? GREEN : '#dc2626', flexShrink: 0 }}>{q.correct_yn ? '✓' : '✗'}</span>
                                                                                                            <p style={{ fontSize: 12, fontWeight: 600, margin: 0 }}>{q.question}</p>
                                                                                                        </div>
                                                                                                        <div style={{ display: 'flex', gap: 6, paddingLeft: 20, flexWrap: 'wrap' }}>
                                                                                                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(74,222,128,0.15)', color: GREEN, fontWeight: 600 }}>✓ {q.correct}</span>
                                                                                                            {!q.correct_yn && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(248,113,113,0.15)', color: RED, fontWeight: 600 }}>✗ {q.student_answer || '—'}</span>}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        </motion.div>
                                                                                    </td>
                                                                                </tr>
                                                                            )}
                                                                        </>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Written Reflections (Homework) */}
                                                {(course.essay_mode === 'essay_per_module' || (course.submissions && course.submissions.length > 0) || (course.pending_reflections && course.pending_reflections.length > 0)) && (
                                                    <div style={{ marginTop: 28 }}>
                                                        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#aaa', marginBottom: 14 }}>✍️ {t('church.member.reflectionsTitle', 'Written Reflections (Homework)')}</h3>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                            {course.pending_reflections?.map((modNum) => (
                                                                <div
                                                                    key={`pending-${modNum}`}
                                                                    style={{
                                                                        background: 'rgba(200, 168, 75, 0.06)',
                                                                        border: '1px solid rgba(200, 168, 75, 0.25)',
                                                                        borderRadius: 12,
                                                                        padding: '14px 18px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        gap: 12,
                                                                    }}
                                                                >
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                        <span style={{ fontSize: 18 }}>📝</span>
                                                                        <div>
                                                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>
                                                                                {t('church.member.reflectionPending', { module: modNum })}
                                                                            </div>
                                                                            <div style={{ fontSize: 11, color: '#888' }}>
                                                                                Module {modNum} completed · Essay submission pending
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <motion.button
                                                                        whileHover={{ scale: 1.03 }}
                                                                        whileTap={{ scale: 0.97 }}
                                                                        onClick={() => {
                                                                            setSelectedProgressCourse(null);
                                                                            navigate(`/church/session/${course.class_id}?module=${modNum}&lesson=1&reflection=1`);
                                                                        }}
                                                                        style={{
                                                                            background: GOLD,
                                                                            color: DARK,
                                                                            border: 'none',
                                                                            padding: '8px 16px',
                                                                            borderRadius: 8,
                                                                            fontSize: 12,
                                                                            fontWeight: 700,
                                                                            cursor: 'pointer',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 4,
                                                                        }}
                                                                    >
                                                                        {t('church.member.writeReflection', 'Write Reflection')} →
                                                                    </motion.button>
                                                                </div>
                                                            ))}

                                                            {course.submissions?.map((sub, sIdx) => (
                                                                <div
                                                                    key={`sub-${sIdx}`}
                                                                    style={{
                                                                        background: 'rgba(255, 255, 255, 0.03)',
                                                                        border: `1px solid ${BORDER}`,
                                                                        borderRadius: 12,
                                                                        padding: '14px 18px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        gap: 12,
                                                                    }}
                                                                >
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                        <span style={{ fontSize: 18 }}>✅</span>
                                                                        <div>
                                                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>
                                                                                Module {sub.module_id} Reflection
                                                                            </div>
                                                                            <div style={{ fontSize: 11, color: '#666' }}>
                                                                                {sub.created_at ? new Date(sub.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        {sub.ai_score !== null && sub.max_score ? (
                                                                            <span
                                                                                style={{
                                                                                    fontSize: 12,
                                                                                    fontWeight: 800,
                                                                                    color: scoreColor((sub.ai_score / sub.max_score) * 100),
                                                                                    background: `${scoreColor((sub.ai_score / sub.max_score) * 100)}20`,
                                                                                    padding: '4px 12px',
                                                                                    borderRadius: 20,
                                                                                }}
                                                                            >
                                                                                {t('church.member.reflectionGraded', { score: sub.ai_score, max: sub.max_score })}
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ fontSize: 12, color: '#888' }}>Submitted</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}

                                                            {(!course.pending_reflections || course.pending_reflections.length === 0) &&
                                                                (!course.submissions || course.submissions.length === 0) && (
                                                                    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 14, padding: '24px', textAlign: 'center' }}>
                                                                        <p style={{ color: '#444', fontSize: 13, margin: 0 }}>
                                                                            Complete module quizzes to unlock written reflection prompts.
                                                                        </p>
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </div>
                                                )}
                                            </motion.div>
                                        </motion.div>
                                    );
                                })()}
                            </AnimatePresence>
                        </motion.div>
                    )}
                    <h4 style={{ fontSize: 12, fontWeight: 700, color: '#444', letterSpacing: '1px', marginBottom: 14 }}>{t('church.member.availableToStart').toUpperCase()}</h4>
                    {(activeTab === 'core_courses' || activeTab === 'further') && (() => {
                        const list = activeTab === 'core_courses' ? bibleschoolAvailable : furtherAvailable;
                        const subtitle = activeTab === 'core_courses'
                            ? t('church.member.coreSubtitle', 'The 9-course foundational Bible curriculum')
                            : t('church.member.furtherSubtitle');

                        return (
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                            >
                                <div style={{ marginBottom: 20, fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
                                    {list.length} {t('church.member.statsCourses').toLowerCase()} · {subtitle}
                                </div>

                                {list.filter(c => c.is_enrolled).length > 0 && (
                                    <div style={{ marginBottom: 32 }}>
                                        <h4 style={{ fontSize: 12, fontWeight: 700, color: GOLD, letterSpacing: '1px', marginBottom: 14 }}>✅ {t('church.member.enrolled').toUpperCase()}</h4>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(360px, 1fr))',
                                            gap: 20
                                        }}>
                                            {list.filter(c => c.is_enrolled).map((c, idx) => (
                                                <CourseBrowseCard key={c.id} course={c} onEnrol={() => { }} enrolling={false} enrolled idx={idx} navigate={navigate} isMobile={isMobile} bibleSchool={bibleSchool} onUpgrade={() => startCheckout('personal')} />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'core_courses' && list.filter(c => !c.is_enrolled).length > 0 && (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                                        gap: 16
                                    }}>
                                        {list.filter(c => !c.is_enrolled).map((c, idx) => (
                                            <CourseBrowseCard
                                                key={c.id}
                                                course={c}
                                                onEnrol={() => enrolInCourse(c.id)}
                                                enrolling={enrolling === c.id}
                                                enrolled={c.is_enrolled}
                                                idx={idx}
                                                navigate={navigate}
                                                isMobile={isMobile}
                                                bibleSchool={bibleSchool}
                                                onUpgrade={() => startCheckout('personal')}
                                            />
                                        ))}
                                    </div>
                                )}

                                {activeTab === 'further' && pathGroups.map(group => (
                                    <div key={group.slug} style={{ marginBottom: 40 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                            <div style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.1)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <GraduationCap size={18} color="#fff" />
                                            </div>
                                            <h3 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: '#fff' }}>{group.title}</h3>
                                            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>({group.courses.length})</span>
                                        </div>
                                        {group.description && <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, margin: '0 0 16px 44px' }}>{group.description}</p>}
                                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                                            {group.courses.map((c, idx) => (
                                                <CourseBrowseCard
                                                    key={c.id}
                                                    course={c}
                                                    onEnrol={() => enrolInCourse(c.id)}
                                                    enrolling={enrolling === c.id}
                                                    enrolled={c.is_enrolled}
                                                    idx={idx}
                                                    navigate={navigate}
                                                    isMobile={isMobile}
                                                    bibleSchool={bibleSchool}
                                                    onUpgrade={() => startCheckout('personal')}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </motion.div>
                        );
                    })()}
                    {activeTab === 'subscription' && (
                        <motion.div
                            key="subscription"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
                        >
                            <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 24, padding: isMobile ? '24px' : '40px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                                    <div style={{ width: 48, height: 48, background: GOLD, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <span style={{ fontSize: 24 }}>💳</span>
                                    </div>
                                    <div>
                                        <h2 style={{ fontSize: 24, fontWeight: 800, margin: 0, color: '#fff' }}>{t('church.member.subscriptionTitle')}</h2>
                                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, margin: 0 }}>{t('church.member.manageAccess')}</p>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
                                    {/* STATUS CARD */}
                                    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 20, padding: '24px' }}>
                                        <h3 style={{ fontSize: 14, fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 16 }}>{t('church.member.currentStatus')}</h3>

                                        {userProfile?.subscription_status === 'premium' || bibleSchool ? (
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: GREEN }} />
                                                    <span style={{ fontWeight: 700, fontSize: 18 }}>{bibleSchool ? t('church.member.churchSeatActive', 'Bible Journey Active') : t('church.member.premiumActive')}</span>
                                                </div>
                                                <p style={{ fontSize: 14, color: '#888', lineHeight: 1.5, marginBottom: 24 }}>
                                                    {bibleSchool ? t('church.member.churchSeatActiveDesc', 'You have full access to the Bible Journey catalog.') : t('church.member.premiumActiveDesc')}
                                                </p>
                                                <button
                                                    onClick={openPortal}
                                                    disabled={loadingPlan !== null}
                                                    style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 14, width: '100%' }}
                                                >
                                                    {loadingPlan === 'portal' ? t('church.member.opening') : t('church.member.managePersonal')}
                                                </button>
                                            </div>
                                        ) : (
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: GOLD }} />
                                                    <span style={{ fontWeight: 700, fontSize: 18 }}>{t('church.member.churchSeat')}</span>
                                                </div>
                                                <p style={{ fontSize: 14, color: '#888', lineHeight: 1.5, marginBottom: 12 }}>
                                                    {(() => {
                                                        const desc = t('church.member.churchSeatDesc');
                                                        return desc.includes('church.member.churchSeatDesc') ? 'You are currently using one of the seats purchased by your church.' : desc;
                                                    })()}
                                                </p>
                                                <div style={{ fontSize: 13, color: GOLD, fontWeight: 600, padding: '8px 12px', background: 'rgba(200,168,75,0.1)', borderRadius: 8 }}>
                                                    {t('church.member.sponsoredBy')} {churchName}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* UPGRADE CARD */}
                                    {!bibleSchool && userProfile?.subscription_status !== 'premium' && (
                                        <div style={{ background: `linear-gradient(135deg, rgba(200,168,75,0.15) 0%, rgba(200,168,75,0.05) 100%)`, border: `1px solid ${GOLD}40`, borderRadius: 20, padding: '24px' }}>
                                            <h3 style={{ fontSize: 14, fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
                                                🔓 {t('church.member.unlockFaith', 'Unlock Full Bible Journey')}
                                            </h3>
                                            <p style={{ fontSize: 14, color: '#aaa', lineHeight: 1.5, marginBottom: 10 }}>
                                                {t('church.member.unlockFaithDesc', 'Get access to all 31 courses in our structured Bible Journey curriculum. Course 1 is always free.')}
                                            </p>
                                            <div style={{ fontSize: 28, fontWeight: 900, color: GOLD, marginBottom: 16 }}>
                                                $7 <span style={{ fontSize: 13, fontWeight: 500, color: '#888' }}>/ month</span>
                                            </div>
                                            <button
                                                onClick={() => startCheckout('personal')}
                                                disabled={loadingPlan !== null}
                                                style={{ background: GOLD, color: DARK, border: 'none', padding: '12px 20px', borderRadius: 12, cursor: 'pointer', fontWeight: 800, fontSize: 14, width: '100%', boxShadow: '0 4px 12px rgba(200,168,75,0.3)' }}
                                            >
                                                {loadingPlan === 'premium' ? t('church.member.connecting') : t('church.member.subscribeSeven', 'Subscribe — $7/mo')}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* SPONSORSHIP SECTION */}
                                <div style={{ marginTop: 32, padding: '24px', background: 'rgba(255,255,255,0.02)', border: `1px dashed ${BORDER}`, borderRadius: 20 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                        <span style={{ fontSize: 24 }}>🎁</span>
                                        <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#fff' }}>{t('church.member.sponsorTitle')}</h3>
                                    </div>
                                    <p style={{ fontSize: 14, color: '#888', lineHeight: 1.5, marginBottom: 16, maxWidth: 600 }}>
                                        {t('church.member.sponsorDesc', { churchName })}
                                    </p>
                                    <div style={{ fontSize: 13, color: GOLD, fontWeight: 600, marginBottom: 16 }}>
                                        ${seatPrice.toFixed(2)} / seat / month
                                    </div>

                                    <div style={{ marginBottom: 20 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                                            <input
                                                type="checkbox"
                                                checked={isSelfManaged}
                                                onChange={(e) => setIsSelfManaged(e.target.checked)}
                                                style={{ width: 18, height: 18, accentColor: GOLD }}
                                            />
                                            <span style={{ color: isSelfManaged ? GOLD : '#ccc', fontWeight: isSelfManaged ? '600' : 'normal' }}>
                                                {t('church.member.sponsorManageMyself')}
                                            </span>
                                        </label>
                                        <p style={{ fontSize: 12, color: '#777', marginTop: 6, marginLeft: 28, maxWidth: 400 }}>
                                            {isSelfManaged
                                                ? t('church.member.sponsorManageMyselfDesc')
                                                : t('church.member.sponsorPoolDesc')}
                                        </p>
                                    </div>

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
                                        {[1, 2, 5, 10].map(qty => (
                                            <motion.button
                                                key={qty}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() => startCheckout('sponsorship', qty)}
                                                disabled={loadingPlan !== null}
                                                style={{
                                                    background: 'rgba(200,168,75,0.1)',
                                                    border: `1px solid ${GOLD}30`,
                                                    color: GOLD,
                                                    padding: '12px 24px',
                                                    borderRadius: 14,
                                                    cursor: 'pointer',
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    minWidth: 100
                                                }}
                                            >
                                                <span style={{ fontSize: 18 }}>{qty} Seat{qty > 1 ? 's' : ''}</span>
                                                <span style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>
                                                    {loadingPlan === 'sponsorship' ? '...' : `$${(qty * seatPrice).toFixed(2)}/mo`}
                                                </span>
                                            </motion.button>
                                        ))}
                                    </div>

                                    {/* MANAGED SEATS DASHBOARD */}
                                    {sponsoredSeats > 0 ? (
                                        <div style={{ marginTop: 24, padding: '20px', background: 'rgba(0,0,0,0.2)', borderRadius: 16, border: `1px solid ${GOLD}40` }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                                <h4 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: GOLD }}>{t('church.member.managedSeatsTitle')}</h4>
                                                <div style={{ fontSize: 13, background: 'rgba(200,168,75,0.15)', padding: '4px 10px', borderRadius: 12, color: GOLD }}>
                                                    {t('church.member.seatsUsed', { used: sponsoredMembers.length, total: sponsoredSeats })}
                                                </div>
                                            </div>

                                            {sponsoredMembers.length > 0 ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                                                    {sponsoredMembers.map((m, i) => (
                                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: CARD, borderRadius: 10, border: `1px solid ${BORDER}` }}>
                                                            <div>
                                                                <div style={{ fontSize: 14, fontWeight: 600 }}>{m.name || 'Member'}</div>
                                                                <div style={{ fontSize: 12, color: '#888' }}>{m.email}</div>
                                                            </div>
                                                            <button
                                                                onClick={() => navigate(`/church/member/${m.id}`)}
                                                                style={{ background: 'transparent', border: `1px solid ${BORDER}`, color: '#ccc', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>
                                                                {t('church.member.viewProgress')}
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
                                                    {t('church.member.noSponsorships')}
                                                </p>
                                            )}

                                            {sponsoredMembers.length < sponsoredSeats ? (
                                                <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}>
                                                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#ccc' }}>
                                                        ✉️ {t('church.member.inviteSomeone')}
                                                    </div>
                                                    <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                                                        {t('church.member.inviteSomeoneDesc')}
                                                    </p>
                                                    <form onSubmit={async (e) => {
                                                        e.preventDefault();
                                                        if (!sponsorEmail || !user || !userProfile?.facility_id) return;
                                                        setInvitingSponsor(true);
                                                        try {
                                                            const { data: { session } } = await supabase.auth.getSession();
                                                            const { error } = await supabase.functions.invoke('invite-resident', {
                                                                body: { email: sponsorEmail, name: sponsorEmail.split('@')[0], sponsor_id: user.id },
                                                                headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
                                                            });
                                                            if (error) throw error;
                                                            setMessage({ type: 'success', text: t('church.member.inviteSent', { email: sponsorEmail }) });
                                                            setSponsorEmail('');
                                                            await loadData();
                                                        } catch (err) {
                                                            console.error('Sponsor invite error:', err);
                                                            setMessage({ type: 'error', text: t('church.member.inviteFailed') });
                                                        } finally {
                                                            setInvitingSponsor(false);
                                                        }
                                                    }} style={{ display: 'flex', gap: 8 }}>
                                                        <input
                                                            type="email"
                                                            value={sponsorEmail}
                                                            onChange={e => setSponsorEmail(e.target.value)}
                                                            placeholder="friend@email.com"
                                                            required
                                                            style={{
                                                                flex: 1, padding: '10px 14px', borderRadius: 10,
                                                                border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.04)',
                                                                color: '#fff', fontSize: 14, outline: 'none'
                                                            }}
                                                        />
                                                        <motion.button
                                                            type="submit"
                                                            whileHover={{ scale: 1.02 }}
                                                            disabled={invitingSponsor || !sponsorEmail}
                                                            style={{
                                                                background: GOLD, color: DARK, border: 'none',
                                                                padding: '10px 20px', borderRadius: 10,
                                                                fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                                                opacity: invitingSponsor ? 0.6 : 1
                                                            }}
                                                        >
                                                            {invitingSponsor ? '...' : t('church.member.sendInvite')}
                                                        </motion.button>
                                                    </form>
                                                </div>
                                            ) : (
                                                <div style={{ borderTop: `1px solid ${GOLD}40`, paddingTop: 16 }}>
                                                    <p style={{ fontSize: 12, color: GOLD, margin: 0 }}>
                                                        {t('church.member.allSeatsUsed')}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 20, marginTop: 8 }}>
                                            <p style={{ fontSize: 13, color: '#888', fontStyle: 'italic', margin: 0 }}>
                                                {t('church.member.managedSeatsHint')}
                                            </p>
                                        </div>
                                    )}

                                    <p style={{ fontSize: 11, color: '#555', marginTop: 16 }}>
                                        {t('church.member.sponsorshipDisclaimer')}
                                    </p>
                                </div>

                                {/* INVITE FRIENDS SECTION */}
                                <div style={{ marginTop: 24, padding: '24px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${BORDER}`, borderRadius: 20 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                        <span style={{ fontSize: 24 }}>✉️</span>
                                        <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t('church.member.inviteFriendsTitle')}</h3>
                                    </div>
                                    <p style={{ fontSize: 14, color: '#888', lineHeight: 1.5, marginBottom: 16, maxWidth: 600 }}>
                                        {t('church.member.inviteFriendsDesc')}
                                    </p>

                                    <form onSubmit={async (e) => {
                                        e.preventDefault();
                                        if (!freeInviteEmail || !user || !userProfile?.facility_id) return;
                                        setSendingFreeInvite(true);
                                        try {
                                            const { data: { session } } = await supabase.auth.getSession();
                                            const { error } = await supabase.functions.invoke('invite-resident', {
                                                body: { email: freeInviteEmail, name: freeInviteEmail.split('@')[0], sponsor_id: user.id, self_pay: true },
                                                headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
                                            });
                                            if (error) throw error;
                                            setMessage({ type: 'success', text: t('church.member.inviteSent', { email: freeInviteEmail }) });
                                            setFreeInviteEmail('');
                                        } catch (err) {
                                            console.error('Free invite error:', err);
                                            setMessage({ type: 'error', text: t('church.member.inviteFailed') });
                                        } finally {
                                            setSendingFreeInvite(false);
                                        }
                                    }} style={{ display: 'flex', gap: 8, maxWidth: 600 }}>
                                        <input
                                            type="email"
                                            value={freeInviteEmail}
                                            onChange={e => setFreeInviteEmail(e.target.value)}
                                            placeholder="friend@email.com"
                                            required
                                            style={{
                                                flex: 1, padding: '10px 14px', borderRadius: 10,
                                                border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.04)',
                                                color: '#fff', fontSize: 14, outline: 'none'
                                            }}
                                        />
                                        <motion.button
                                            type="submit"
                                            whileHover={{ scale: 1.02 }}
                                            disabled={sendingFreeInvite || !freeInviteEmail}
                                            style={{
                                                background: 'transparent', color: '#fff', border: `1px solid ${BORDER}`,
                                                padding: '10px 20px', borderRadius: 10,
                                                fontWeight: 600, fontSize: 13, cursor: 'pointer',
                                                opacity: sendingFreeInvite ? 0.6 : 1
                                            }}
                                        >
                                            {sendingFreeInvite ? '...' : t('church.member.sendFreeInvite')}
                                        </motion.button>
                                    </form>
                                </div>
                            </div>

                            {/* TOAST MESSAGE */}
                            <AnimatePresence>
                                {message && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        style={{
                                            position: 'fixed', bottom: 32, right: 32, zIndex: 100,
                                            background: message.type === 'success' ? GREEN : RED,
                                            color: '#fff', padding: '16px 24px', borderRadius: 12,
                                            fontWeight: 700, boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        {message.text}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    )}

                </AnimatePresence>
            </div>
        </motion.div>
    );
}


function CourseBrowseCard({ course, onEnrol, enrolling, enrolled, idx, navigate, isMobile, bibleSchool, onUpgrade }: {
    course: AvailableCourse; onEnrol: () => void; enrolling: boolean; enrolled: boolean; idx: number;
    navigate: (path: string, opts?: Record<string, unknown>) => void; isMobile: boolean;
    bibleSchool: boolean;
    onUpgrade?: () => void;
}) {
    const { t } = useTranslation();

    // Determine gate status for this card
    const isFree = isFreeFaithCategory(course.category_id);
    const locked = course.curriculum_track !== 'faith' && !bibleSchool;

    return (
        <motion.div
            custom={idx}
            variants={{ hidden: { opacity: 0, y: 16 }, visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06 } }) }}
            initial="hidden"
            animate="visible"
            whileHover={{ y: locked ? 0 : -3 }}
            style={{
                background: enrolled ? 'rgba(200,168,75,0.07)' : locked ? 'rgba(255,255,255,0.02)' : CARD,
                border: `${enrolled ? '1.5px' : '1px'} solid ${enrolled ? 'rgba(200,168,75,0.3)' : locked ? 'rgba(255,255,255,0.05)' : BORDER}`,
                borderRadius: 24,
                padding: isMobile ? '20px' : '28px',
                display: 'flex', alignItems: 'flex-start', gap: isMobile ? 16 : 22,
                transition: 'transform 0.15s, box-shadow 0.15s',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                position: 'relative',
                overflow: 'hidden',
                opacity: locked ? 0.75 : 1,
            }}
        >
            {/* LOCK OVERLAY for gated courses */}
            {locked && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(135deg, rgba(10,18,32,0.7) 0%, rgba(10,18,32,0.5) 100%)',
                        borderRadius: 24, zIndex: 10,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 12,
                        backdropFilter: 'blur(2px)',
                    }}
                >
                    <div style={{
                        width: 48, height: 48, borderRadius: '50%',
                        background: 'rgba(200,168,75,0.15)',
                        border: `1.5px solid ${GOLD}60`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Lock size={20} color={GOLD} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 2 }}>
                            {t('church.member.lockedCourse', 'Full access at')}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 900, color: GOLD }}>
                            $7<span style={{ fontSize: 11, fontWeight: 500, color: '#888' }}>/mo</span>
                        </div>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={(e) => { e.stopPropagation(); onUpgrade?.(); }}
                        style={{
                            background: GOLD, color: DARK, border: 'none',
                            padding: '8px 20px', borderRadius: 10,
                            fontSize: 12, fontWeight: 800, cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(200,168,75,0.3)',
                        }}
                    >
                        {t('church.member.subscribeSeven', 'Subscribe — $7/mo')}
                    </motion.button>
                </motion.div>
            )}

            <ChurchIcon size={42} />
            <div style={{ flex: 1, minWidth: 0 }}>
                {/* Free badge */}
                {isFree && !enrolled && (
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#34d399', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>
                        ✦ {t('church.member.freeCourse', 'Free — Start Here')}
                    </div>
                )}
                {course.curriculum_track === 'faith' && !isFree && (() => {
                    const num = course.id.slice(-2);
                    const courseNum = parseInt(num) > 0 ? parseInt(num) : null;
                    return courseNum ? (
                        <div style={{ fontSize: 10, fontWeight: 800, color: GOLD, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 2 }}>
                            {t('church.member.courseNum', { num: courseNum })}
                        </div>
                    ) : null;
                })()}
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, lineHeight: 1.3 }}>{course.name}</div>
                {enrolled && course.pending_reflections && course.pending_reflections.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        {course.pending_reflections.map(modNum => (
                            <span
                                key={modNum}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/church/session/${course.id}?module=${modNum}&lesson=1&reflection=1`);
                                }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    padding: '2px 7px',
                                    borderRadius: 6,
                                    background: 'rgba(200, 168, 75, 0.12)',
                                    border: '1px solid rgba(200, 168, 75, 0.35)',
                                    color: GOLD,
                                    cursor: 'pointer',
                                }}
                                title={t('church.member.writeReflection')}
                            >
                                📝 {t('church.member.reflectionPending', { module: modNum })} →
                            </span>
                        ))}
                    </div>
                )}
                {course.description && (
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, marginBottom: 10 }}>
                        {course.description.slice(0, 90)}{course.description.length > 90 ? '…' : ''}
                    </div>
                )}
                {(Boolean(course.module_count) || (course.module_plan && course.module_plan.length > 0)) && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(200,168,75,0.1)', color: GOLD, padding: '3px 8px', borderRadius: 100, border: '1px solid rgba(200,168,75,0.2)' }}>
                            📚 {t('church.member.modulesCount', { count: course.module_count || course.module_plan?.length || 0 })}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.4)', padding: '3px 8px', borderRadius: 100, border: '1px solid rgba(255,255,255,0.07)' }}>
                            {t('church.member.lessonsCount', { count: course.lesson_count || (course.module_plan ? course.module_plan.reduce((s: number, m: any) => s + (m.lessons?.length || 0), 0) : 0) })}
                        </span>
                    </div>
                )}
                {!locked && (
                    enrolled ? (
                        <motion.button
                            whileHover={{ scale: 1.03 }}
                            onClick={() => navigate(`/church/session/${course.id}?module=${course.progress?.current_unit || 1}&lesson=${course.progress?.current_lesson || 1}`)}
                            style={{ background: GOLD, color: DARK, border: 'none', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                        >
                            {t('church.member.continue')} →
                        </motion.button>
                    ) : (
                        <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={onEnrol}
                            disabled={enrolling}
                            style={{ background: 'rgba(200,168,75,0.15)', color: GOLD, border: '1px solid rgba(200,168,75,0.3)', padding: '7px 16px', borderRadius: 8, cursor: enrolling ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600, opacity: enrolling ? 0.6 : 1 }}
                        >
                            {enrolling ? '…' : t('church.member.startCourse')}
                        </motion.button>
                    )
                )}
            </div>
        </motion.div>
    );
}
